import Icon from "@mui/material/Icon";
import { styled } from "@mui/material/styles";
import { black, blue, grey, orange, white } from "designSystem/styles/color";
import { Chip, CsiImage, OverflowText } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import ContextConstructor from "csi/def/Context";
import html from "csi/html";
import { isUnreadNotification, updateNotifications } from "csi/service/notificationService";
import { useNotificationStore } from "csi/service/NotificationStore";
import { C8Node } from "csi/typings";
import xml from "csi/xml";
import { throttle } from "lodash";
import React, { FunctionComponent, useRef } from "react";
import csiCard from "src/csi/card/_base";
import csiHeading from "src/csi/heading/_base";
import { getImageSrc } from "src/csi/model/image";
import TaskUtil from "src/csi/task/util";
import types from "src/csi/types";
import ReadUnreadBtn from "./ReadUnreadBtn";

export interface TaskCardProps {
	id: number;
	task: C8Node;
	isAssignedTask: boolean; // Taks's assigned / unassigned flag
}

const ICON_MAP = {
	1: "critical",
	2: "high",
	3: "medium",
	4: "low",
	5: "minor",
};

const PREFIX = "TaskCard";
const classes = {
	taskCardNew: `${PREFIX}taskCardNew`,
	image40: `${PREFIX}image40`,
	imagePlaceholder: `${PREFIX}imagePlaceholder`,
	infoPane: `${PREFIX}infoPane`,
	firstRow: `${PREFIX}firstRow`,
	taskPillAvailable: `${PREFIX}taskPillAvailable`,
	taskPillAssigned: `${PREFIX}taskPillAssigned`,
	dateAssigned: `${PREFIX}dateAssigned`,
	dueDate: `${PREFIX}dueDate`,
	secondRow: `${PREFIX}secondRow`,
	priorityBadge: `${PREFIX}priorityBadge`,
	taskName: `${PREFIX}taskName`,
	thirdRow: `${PREFIX}thirdRow`,
	separator: `${PREFIX}separator`,
};

const Root = styled("div")((
	{
		theme,
	},
) => ({
	width: 496,
	padding: 12,
	borderBottom: "1px solid rgba(0, 0, 0, .05)",
	boxSizing: "border-box",
	display: "flex",
	flexDirection: "row",
	background: white[50],
	position: "relative",
	"&:hover": {
		background: grey[200],
	},
	"&:active": {
		background: grey[300],
	},
	[`&.${classes.taskCardNew}`]: {
		background: blue[50],
	},
	[`& .${classes.image40}`]: {
		maxHeight: 40,
		maxWidth: 40,
		height: 40,
		width: 40,
		border: `1px solid ${grey[300]}`,
		borderRadius: 4,
		background: grey[50],
		marginRight: 12,
		display: "flex",
		alignItems: "center",
	},
	[`& .${classes.imagePlaceholder}`]: {
		color: `${grey[600]} !important`,
		fontSize: "20px !important",
		padding: "1px 0",
	},
	[`& .${classes.infoPane}`]: {
		display: "flex",
		flexDirection: "column",
		width: 395,
	},
	[`& .${classes.firstRow}`]: {
		height: 16,
	},
	[`& .${classes.taskPillAvailable}`]: {
		background: white[50],
		border: `1px solid ${grey[400]}`,
		height: 16,
		fontWeight: 600,
		"& span": {
			color: `${black[100]} !important`,
			fontSize: 10,
			paddingTop: 0,
			paddingBottom: 0,
			maxWidth: 200,
		},
	},
	[`& .${classes.taskPillAssigned}`]: {
		background: orange[50],
		border: `1px solid ${orange[100]}`,
		height: 16,
		fontWeight: 600,
		"& span": {
			color: `${orange[900]} !important`,
			fontSize: 10,
			paddingTop: 0,
			paddingBottom: 0,
		},
	},
	[`& .${classes.dateAssigned}`]: {
		fontSize: 10,
		verticalAlign: "middle",
		color: grey[800],
		padding: "0 12px",
		fontWeight: 600,
	},
	[`& .${classes.dueDate}`]: {
		float: "right",
		fontSize: 10,
		fontWeight: "bold",
		paddingTop: 2,
	},
	[`& .${classes.secondRow}`]: {
		marginBottom: 4,
		marginTop: 8,
		display: "flex",
	},
	[`& .${classes.priorityBadge}`]: {
		height: 16,
		width: 16,
		minWidth: 16,
		display: "inline-block",
		marginRight: 4,
		verticalAlign: "middle",
		"& span": {
			fontSize: 10,
		},
	},
	[`& .${classes.taskName}`]: {
		color: black[100],
		fontSize: 12,
		fontWeight: "bold",
		overflow: "hidden",
		display: "-webkit-box",
		WebkitBoxOrient: "vertical",
		WebkitLineClamp: 3,
	},
	[`& .${classes.thirdRow}`]: {
		color: black[500],
		height: 16,
		display: "flex",
	},
	[`& .${classes.separator}`]: {
		margin: "0 3px",
	},
}));

// Two attributes on the third row share the same space (divided by "|" separator)
// If one attribute is small and occupy less then half - other attribute will be allowed to use all available space
// However, smaller attribute should always be fully shown, no truncation in favor of larger attribute
// This function create style min-width string which guarantee that smaller element will not be cut in favor of larger cmponent
const getMinWidth = (text?: string) => {
	text = text || "";
	const width = Math.ceil(html.getTextWidth(text) + 1);
	return `min(${width}px, 50% - 4px)`; // 4px offset is to leave space for separator between two components
};

export const TaskCard: FunctionComponent<TaskCardProps> = (params) => {
	const { id, isAssignedTask, task } = params;
	const locale = useLocale();
	const notificationStore = useNotificationStore();
	const { cache } = notificationStore;
	const notification = notificationStore.getById(id);

	const c8Instance = cache.get(task.C8Instance);

	// C8 Instance Image source
	const imageSrc = getImageSrc(c8Instance);

	// Is New Task
	const isNewTask = isUnreadNotification(notification);

	// Slim card's first two attribute's text
	const specs = csiCard.getAttributeCardSlimSpecs(c8Instance.$Type);
	const ctx = new ContextConstructor(null, cache, c8Instance.$URL);
	const attrSpecs = specs.attributeSpecs;
	const attrNum = attrSpecs.length;
	const attr1 = (attrNum > 0) ? csiHeading.toText(ctx, specs.attributeSpecs[0].heading) : "";
	const attr2 = (attrNum > 1) ? csiHeading.toText(ctx, specs.attributeSpecs[1].heading) : "";

	// Date assigned
	const assignedDate = types.formatTime(notification.createdAt, types.FORMAT_DATETIME);

	// Priority icon
	const priorityText = xml.enumGetText(task.Priority);
	const priorityIcon = ICON_MAP[priorityText];
	const priorityCSS = "csi-custom-icon csi-custom-icon-" + priorityIcon;

	// Task pill text (Assigned/Available)
	const taskPill = isAssignedTask ? locale.resource("CamundaTaskAssignedToMe") : locale.resource("CamundaTaskAvailable");

	// Due date
	const taskDueDate = task.DueDate;
	const dueDate = taskDueDate ? locale.resource("CamundaTaskDue") + " " + types.getFormattedDateDifference(types.getTimeDate(taskDueDate)) : "";
	const dueCSS = taskDueDate ? " csi-camunda-task-due-" + TaskUtil.getDueDateClassification(types.getTimeDate(taskDueDate), true) : "";

	// throttle server request to prevent frequent server request on each mouse click, prevent "ddos" like behavior.
	// double click is usually 500ms, so 1000 is long enough to cover possible multiple requests and short enough to prevent user inconvenience.
	const updateNotificationsThrottled = useRef(throttle((notificationId, isRead) => {
		updateNotifications([notificationId], isRead);
	}, 1000, { leading: false, trailing: true })).current;

	const onUnreadClick = (e) => {
		notificationStore.setRead(notification.id, isNewTask);
		updateNotificationsThrottled(notification.id, isNewTask);
	};

	return (
		<Root className={"csi-notification-card " + (isNewTask ? "  " + classes.taskCardNew : "")}>
			<CsiImage
				className={classes.image40}
				noImageComponent={
					<Icon className={classes.imagePlaceholder}>image</Icon>
				}
				src={imageSrc}
			/>
			<div className={classes.infoPane}>
				<div className={classes.firstRow}>
					<Chip className={params.isAssignedTask ? classes.taskPillAssigned : classes.taskPillAvailable} label={taskPill} />
					<span className={classes.dateAssigned}>{assignedDate}</span>
					<span className={classes.dueDate + dueCSS}>{dueDate}</span>
				</div>
				<div className={classes.secondRow}>
					<span className={classes.priorityBadge + " " + priorityCSS} />
					<span className={classes.taskName}>{locale.getNodeName(task)}</span>
				</div>
				<div className={classes.thirdRow}>
					<OverflowText style={{ minWidth: getMinWidth(attr1) }} text={attr1} />
					{attr2 ? <span className={classes.separator}>|</span> : ""}
					<OverflowText style={{ minWidth: getMinWidth(attr2) }} text={attr2} />
				</div>
				<ReadUnreadBtn isUnread={isNewTask} onClick={onUnreadClick} />
			</div>
		</Root>
	);
};